import { useState } from "react";

/**
 * Super admin preferences kept in this browser only (localStorage): the
 * modules shown in the menu, the filters of the list pages. Nothing on the
 * server, nothing shared between super admins.
 */
const STORAGE_PREFIX = "baback:super-admin:";

function read<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Private browsing or quota: the preference simply does not persist.
  }
}

export const ADMIN_MODULES = [
  { value: "global", label: "Configuration globale" },
  { value: "returns", label: "Returns" },
  { value: "tracking", label: "Tracking" },
] as const;

export type AdminModule = (typeof ADMIN_MODULES)[number]["value"];

const ALL_MODULES: AdminModule[] = ADMIN_MODULES.map(({ value }) => value);

/** Modules whose pages the menu shows; every module by default. */
export function useAdminModules(): [
  AdminModule[],
  (next: AdminModule[]) => void,
] {
  const [modules, setModules] = useState<AdminModule[]>(() =>
    read<AdminModule[]>("modules", ALL_MODULES).filter((module) =>
      ALL_MODULES.includes(module),
    ),
  );

  function update(next: AdminModule[]) {
    setModules(next);
    write("modules", next);
  }

  return [modules, update];
}
